import React from 'react';
import { CircuitRenderer, CircuitElement, Point } from './CircuitRenderer';

interface CurrentDiagramProps {
    variant: 'node' | 'branch' | 'divider';
    unknownIndex?: number;
    currents?: number[];
    directions?: ('into' | 'out of')[];
    totalCurrent?: number;
    branchCurrents?: number[];
    r1?: number;
    r2?: number;
    i1?: number;
    i2?: number;
}

// Standardized diagram dimensions
const DIAGRAM_WIDTH = 450;
const DIAGRAM_HEIGHT = 350;

export const CurrentDiagram: React.FC<CurrentDiagramProps> = ({
    variant,
    unknownIndex,
    currents,
    directions,
    totalCurrent,
    branchCurrents,
    r1, r2, i1, i2
}) => {
    const width = DIAGRAM_WIDTH;
    const height = DIAGRAM_HEIGHT;
    const centerX = width / 2;
    const centerY = height / 2;
    const blue = "#2563eb";

    let elements: CircuitElement[] = [];
    let junctions: Point[] = [];

    if (variant === 'node') {
        // Number of currents determines layout (3-4 typical)
        // Angles: Up (-90), Left (180), Right (0), Down (90)
        const armLength = 100;
        const positions = [
            { x: centerX, y: centerY - armLength }, // Top
            { x: centerX - armLength, y: centerY }, // Left
            { x: centerX + armLength, y: centerY }, // Right
            { x: centerX, y: centerY + armLength }, // Bottom
        ];

        junctions.push({ x: centerX, y: centerY });

        currents?.forEach((val, i) => {
            const dir = directions?.[i] || 'into';
            const pos = positions[i];
            const isUnknown = i === unknownIndex;
            const label = isUnknown ? `I${i + 1} = ?` : `I${i + 1} = ${val}A`;
            const labelColor = isUnknown ? blue : undefined;

            let from: Point, to: Point;

            if (dir === 'into') {
                from = pos; to = { x: centerX, y: centerY };
            } else {
                from = { x: centerX, y: centerY }; to = pos;
            }

            elements.push({
                type: 'currentArrow',
                from, to,
                label,
                labelColor,
                labelOffset: 40
            });
        });

    } else if (variant === 'branch') {
        const bTopY = 70;
        const bBottomY = 280;
        const startX = 50;
        const splitX = 130;
        const endX = 400;

        // Input
        elements.push({
            type: 'currentArrow',
            from: { x: startX, y: bTopY },
            to: { x: splitX, y: bTopY },
            label: `It = ${totalCurrent}A`,
            labelOffset: 20
        });

        // Rails
        elements.push({ type: 'wire', from: { x: splitX, y: bTopY }, to: { x: endX, y: bTopY } });
        elements.push({ type: 'wire', from: { x: Math.max(0, splitX - 50), y: bBottomY }, to: { x: endX, y: bBottomY } });

        branchCurrents?.forEach((val, i) => {
            const bx = splitX + 50 + (i * 100);
            const isUnknown = i === unknownIndex;
            const label = isUnknown ? `I${i + 1} = ?` : `I${i + 1} = ${val}A`;
            const labelColor = isUnknown ? blue : undefined;

            junctions.push({ x: bx, y: bTopY });
            junctions.push({ x: bx, y: bBottomY });

            elements.push({
                type: 'currentArrow',
                from: { x: bx, y: bTopY },
                to: { x: bx, y: bBottomY },
                label,
                labelColor,
                labelOffset: 35
            });
        });

    } else {
        // Divider
        const bTopY = 70;
        const bBottomY = 280;
        const startX = 50;
        const splitX = 130;
        const endX = 380;

        elements.push({
            type: 'currentArrow',
            from: { x: startX, y: bTopY },
            to: { x: splitX, y: bTopY },
            label: `It = ${totalCurrent}A`,
            labelOffset: 20
        });

        elements.push({ type: 'wire', from: { x: splitX, y: bTopY }, to: { x: endX, y: bTopY } });
        elements.push({ type: 'wire', from: { x: Math.max(0, splitX - 40), y: bBottomY }, to: { x: endX, y: bBottomY } });

        const branch1X = splitX + 60;
        const branch2X = splitX + 160;
        const arrowLength = 50;

        // Branch 1
        junctions.push({ x: branch1X, y: bTopY });
        junctions.push({ x: branch1X, y: bBottomY });

        elements.push({
            type: 'currentArrow',
            from: { x: branch1X, y: bTopY },
            to: { x: branch1X, y: bTopY + arrowLength },
            label: `I1 = ${i1 !== undefined ? i1 + 'A' : '?'}`,
            labelColor: i1 === undefined ? blue : undefined,
            labelOffset: 35
        });

        elements.push({
            type: 'resistor',
            from: { x: branch1X, y: bTopY + arrowLength },
            to: { x: branch1X, y: bBottomY },
            value: r1,
            label: `R1=${r1}Ω`
        });

        // Branch 2
        junctions.push({ x: branch2X, y: bTopY });
        junctions.push({ x: branch2X, y: bBottomY });

        elements.push({
            type: 'currentArrow',
            from: { x: branch2X, y: bTopY },
            to: { x: branch2X, y: bTopY + arrowLength },
            label: `I2 = ${i2 !== undefined ? i2 + 'A' : '?'}`,
            labelColor: i2 === undefined ? blue : undefined,
            labelOffset: 35
        });

        elements.push({
            type: 'resistor',
            from: { x: branch2X, y: bTopY + arrowLength },
            to: { x: branch2X, y: bBottomY },
            value: r2,
            label: `R2=${r2}Ω`
        });
    }

    return <CircuitRenderer width={width} height={height} elements={elements} junctions={junctions} />;
};
